/**
 * In-game trails behind a racer: the skin's trail (TRAILS: classic streak, sparks, rainbow,
 * bubbles, fire) — the same looks as the profile editor previews (ui/skin/trailArt.ts) —
 * and the wave's zigzag line.
 *
 * Points live in world coordinates in fixed-size ring buffers and are drawn without
 * allocating: ribbons are one polygon per stripe filled with a cached horizontal fade.
 */
import type { TrailId } from '@cube-rush/shared';
import { parseHex, rgba } from './color';
import type { ViewTransform } from './view';

const CAPACITY = 24;
/** The trail reaches this far behind the racer, blocks. */
const TRAIL_LENGTH = 3.2;
/** New points are added at least this far apart, blocks. */
const MIN_STEP = 0.08;
const RAINBOW = ['#ff3b5c', '#ff9a1f', '#ffd60a', '#22e07a', '#2de2ff', '#8c3dff'] as const;

/** Deterministic noise in [0, 1) for a point id and a channel (as in trailArt). */
function noise(id: number, channel: number): number {
  let h = Math.imul(id ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(channel + 1, 0xc2b2ae35);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  return (h >>> 0) / 4294967296;
}

/**
 * Horizontal fades (transparent → colour) from x = 0 to x = 1, one per colour and context.
 * A ribbon is drawn under a transform that stretches this unit gradient from the trail's
 * tail to the racer: no new gradient and no clip per frame.
 */
const fades = new WeakMap<CanvasRenderingContext2D, Map<string, CanvasGradient>>();

function fade(ctx: CanvasRenderingContext2D, color: string): CanvasGradient {
  let byColor = fades.get(ctx);
  if (!byColor) {
    byColor = new Map();
    fades.set(ctx, byColor);
  }
  let gradient = byColor.get(color);
  if (!gradient) {
    const [r, g, b] = parseHex(color);
    gradient = ctx.createLinearGradient(0, 0, 1, 0);
    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`);
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0.9)`);
    byColor.set(color, gradient);
  }
  return gradient;
}

export interface TrailColors {
  primary: string;
  secondary: string;
}

/** The skin trail of one racer. */
export class Trail {
  private readonly xs = new Float64Array(CAPACITY);
  private readonly ys = new Float64Array(CAPACITY);
  private readonly ids = new Uint32Array(CAPACITY);
  /** Scratch of measure(): screen positions and band normals, the racer at index `count`. */
  private readonly px = new Float64Array(CAPACITY + 1);
  private readonly py = new Float64Array(CAPACITY + 1);
  private readonly nx = new Float64Array(CAPACITY + 1);
  private readonly ny = new Float64Array(CAPACITY + 1);
  private head = 0;
  private count = 0;
  private nextId = 1;

  get size(): number {
    return this.count;
  }

  clear(): void {
    this.count = 0;
  }

  /** Records the racer's position (once per frame). */
  add(x: number, y: number): void {
    if (this.count > 0) {
      const last = (this.head + this.count - 1) % CAPACITY;
      const dx = x - this.xs[last]!;
      const dy = y - this.ys[last]!;
      if (dx * dx + dy * dy < MIN_STEP * MIN_STEP) return;
      // A jump backwards (respawn, next level) starts a new trail.
      if (dx < -1) this.count = 0;
    }
    if (this.count === CAPACITY) {
      this.head = (this.head + 1) % CAPACITY;
      this.count--;
    }
    const i = (this.head + this.count) % CAPACITY;
    this.xs[i] = x;
    this.ys[i] = y;
    this.ids[i] = this.nextId++;
    this.count++;
    // Forget points too far behind.
    while (this.count > 2 && this.xs[this.head]! < x - TRAIL_LENGTH) {
      this.head = (this.head + 1) % CAPACITY;
      this.count--;
    }
  }

  /** Draws the trail up to the racer at (headX, headY); `size` = racer size in CSS px. */
  /** `scale` = the context's base transform (device pixels per CSS pixel). */
  draw(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    scale: number,
    trail: TrailId,
    colors: TrailColors,
    size: number,
    time: number,
    headX: number,
    headY: number,
  ): void {
    if (this.count < 2 || trail === 'none') return;
    switch (trail) {
      case 'classic':
        this.measure(view, headX, headY);
        this.ribbon(ctx, scale, colors.primary, 1, 0, size * 0.55);
        break;
      case 'rainbow':
        this.measure(view, headX, headY);
        for (let s = 0; s < RAINBOW.length; s++) {
          this.ribbon(ctx, scale, RAINBOW[s]!, RAINBOW.length, s, size * 0.8);
        }
        break;
      case 'sparks':
        this.sparks(ctx, view, colors, size, time);
        break;
      case 'bubbles':
        this.bubbles(ctx, view, colors, size, time);
        break;
      case 'fire':
        this.fire(ctx, view, size, time);
        break;
    }
    ctx.globalAlpha = 1;
  }

  private point(k: number): number {
    return (this.head + k) % CAPACITY;
  }

  /**
   * Screen positions of the points (the racer last) and the unit normals of the band there,
   * from neighbouring points so the band bends smoothly. Computed once per frame for every
   * stripe of a ribbon (no Math.hypot: it allocates in V8).
   */
  private measure(view: ViewTransform, headX: number, headY: number): void {
    const n = this.count;
    for (let k = 0; k <= n; k++) {
      const before = Math.max(0, k - 1);
      const after = Math.min(n, k + 1);
      const dx = this.worldX(after, headX) - this.worldX(before, headX);
      const dy = this.worldY(before, headY) - this.worldY(after, headY);
      const length = Math.sqrt(dx * dx + dy * dy);
      this.px[k] = view.sx(this.worldX(k, headX));
      this.py[k] = view.sy(this.worldY(k, headY));
      if (length > 1e-9) {
        this.nx[k] = -dy / length;
        this.ny[k] = dx / length;
      } else {
        // The racer stands on the newest point: keep the direction (and the band's width).
        this.nx[k] = k > 0 ? this.nx[k - 1]! : 0;
        this.ny[k] = k > 0 ? this.ny[k - 1]! : 1;
      }
    }
  }

  /**
   * Stripe `stripe` of `stripes` of a band `width` px wide that tapers to nothing at the
   * oldest point (positions and normals from measure()).
   */
  private ribbon(
    ctx: CanvasRenderingContext2D,
    scale: number,
    color: string,
    stripes: number,
    stripe: number,
    width: number,
  ): void {
    const n = this.count;
    const band = width / stripes;
    const near = (stripe - stripes / 2) * band;
    const far = near + band;
    const tail = this.px[0]!;
    const span = this.px[n]! - tail;
    // The path is built in the gradient's own space: u = 0 at the tail, 1 at the racer
    // (x stretched by `span`, y in CSS px), so one cached gradient fits every frame.
    const stretched = span >= 1;
    if (stretched) ctx.setTransform(scale * span, 0, 0, scale, scale * tail, 0);
    // One closed outline: forward along one edge, back along the other (k = n is the racer).
    // A second moveTo would split it into two slivers that fill as hairlines.
    ctx.beginPath();
    for (let pass = 0; pass < 2; pass++) {
      const offset = pass === 0 ? near : far;
      for (let j = 0; j <= n; j++) {
        const k = pass === 0 ? j : n - j;
        const shift = (offset * k) / n;
        const sx = this.px[k]! + this.nx[k]! * shift;
        const y = this.py[k]! + this.ny[k]! * shift;
        const x = stretched ? (sx - tail) / span : sx;
        if (pass === 0 && j === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
    }
    ctx.closePath();
    if (!stretched) {
      ctx.fillStyle = rgba(color, 0.6);
      ctx.fill();
      return;
    }
    ctx.fillStyle = fade(ctx, color);
    ctx.fill();
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
  }

  /** World x of point k (k = count: the racer's current position). */
  private worldX(k: number, headX: number): number {
    return k >= this.count ? headX : this.xs[this.point(k)]!;
  }

  private worldY(k: number, headY: number): number {
    return k >= this.count ? headY : this.ys[this.point(k)]!;
  }

  private sparks(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    colors: TrailColors,
    size: number,
    time: number,
  ): void {
    const n = this.count;
    for (let k = 0; k < n; k += 2) {
      const i = this.point(k);
      const id = this.ids[i]!;
      const old = 1 - k / (n - 1);
      const r = size * (0.08 + noise(id, 0) * 0.14) * (1 - old * 0.6);
      const x = view.sx(this.xs[i]!) + (noise(id, 1) - 0.5) * size * 0.8;
      const y =
        view.sy(this.ys[i]!) +
        (noise(id, 2) - 0.5) * size * 0.9 +
        Math.sin(time * 6 + id) * size * 0.05;
      ctx.globalAlpha = (1 - old) * 0.95;
      ctx.fillStyle = noise(id, 3) < 0.5 ? colors.secondary : '#ffffff';
      ctx.beginPath();
      ctx.moveTo(x, y - r * 2);
      ctx.lineTo(x + r * 0.6, y);
      ctx.lineTo(x, y + r * 2);
      ctx.lineTo(x - r * 0.6, y);
      ctx.closePath();
      ctx.fill();
    }
  }

  private bubbles(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    colors: TrailColors,
    size: number,
    time: number,
  ): void {
    const n = this.count;
    ctx.lineWidth = Math.max(1, size * 0.06);
    for (let k = 0; k < n; k += 3) {
      const i = this.point(k);
      const id = this.ids[i]!;
      const old = 1 - k / (n - 1);
      const r = size * (0.12 + noise(id, 0) * 0.16) * (0.6 + old * 0.8);
      const x =
        view.sx(this.xs[i]!) +
        (noise(id, 1) - 0.5) * size * 0.5 +
        Math.sin(time * 3 + id) * size * 0.06;
      const y = view.sy(this.ys[i]!) - old * size * 0.9 + (noise(id, 2) - 0.5) * size * 0.6;
      ctx.globalAlpha = (1 - old) * 0.9;
      ctx.strokeStyle = colors.secondary;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.beginPath();
      ctx.arc(x - r * 0.35, y - r * 0.35, r * 0.22, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private fire(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    size: number,
    time: number,
  ): void {
    const n = this.count;
    for (let k = 0; k < n; k++) {
      const i = this.point(k);
      const id = this.ids[i]!;
      const old = 1 - k / (n - 1);
      const r = size * (0.34 - old * 0.28) * (0.7 + noise(id, 0) * 0.6);
      if (r <= 0) continue;
      const x = view.sx(this.xs[i]!) + (noise(id, 1) - 0.5) * size * 0.35;
      const y = view.sy(this.ys[i]!) - old * size * 0.7 + Math.sin(time * 9 + id) * size * 0.04;
      ctx.globalAlpha = (1 - old) * 0.85;
      ctx.fillStyle = old < 0.25 ? '#fff27a' : old < 0.55 ? '#ffa51f' : '#ff3b2f';
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

const WAVE_CAPACITY = 96;
/** The zigzag stays visible this far behind the wave, blocks. */
const WAVE_LENGTH = 16;

/** The wave's zigzag: a polyline with a corner wherever the wave turned. */
export class WaveTrail {
  private readonly xs = new Float64Array(WAVE_CAPACITY);
  private readonly ys = new Float64Array(WAVE_CAPACITY);
  private head = 0;
  private count = 0;
  /** Direction of the last segment: 1 up, -1 down, 0 flat. */
  private direction = 0;

  clear(): void {
    this.count = 0;
    this.direction = 0;
  }

  get size(): number {
    return this.count;
  }

  /** Records the wave's position (once per frame while it is a wave). */
  add(x: number, y: number): void {
    if (this.count > 0) {
      const last = (this.head + this.count - 1) % WAVE_CAPACITY;
      const dx = x - this.xs[last]!;
      if (dx < -1) this.clear();
    }
    if (this.count >= 2) {
      const last = (this.head + this.count - 1) % WAVE_CAPACITY;
      const dy = y - this.ys[last]!;
      const direction = Math.abs(dy) < 0.004 ? 0 : dy > 0 ? 1 : -1;
      if (direction === this.direction) {
        // Same direction: extend the last segment.
        this.xs[last] = x;
        this.ys[last] = y;
        return;
      }
      this.direction = direction;
    }
    if (this.count === WAVE_CAPACITY) {
      this.head = (this.head + 1) % WAVE_CAPACITY;
      this.count--;
    }
    const i = (this.head + this.count) % WAVE_CAPACITY;
    this.xs[i] = x;
    this.ys[i] = y;
    this.count++;
    while (this.count > 2 && this.xs[(this.head + 1) % WAVE_CAPACITY]! < x - WAVE_LENGTH) {
      this.head = (this.head + 1) % WAVE_CAPACITY;
      this.count--;
    }
  }

  draw(ctx: CanvasRenderingContext2D, view: ViewTransform, color: string): void {
    if (this.count < 2) return;
    ctx.beginPath();
    for (let k = 0; k < this.count; k++) {
      const i = (this.head + k) % WAVE_CAPACITY;
      const x = view.sx(this.xs[i]!);
      const y = view.sy(this.ys[i]!);
      if (k === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.lineJoin = 'miter';
    ctx.lineCap = 'butt';
    ctx.strokeStyle = rgba(color, 0.28);
    ctx.lineWidth = Math.max(6, view.ppb * 0.42);
    ctx.stroke();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(2.5, view.ppb * 0.16);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.lineWidth = Math.max(1, view.ppb * 0.05);
    ctx.stroke();
  }
}
